import { useFormsTranslation } from '@/hooks/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

// Create validation schema with translations
export const createBillingAddressSchema = (tForms: (key: string) => string) =>
  z
    .object({
      // Switch to enable different billing address
      isDifferentFromAccount: z.boolean(),

      // Billing address fields (conditional)
      billingName: z.string().optional(),
      billingCountry: z.string().optional(),
      billingStreet: z.string().optional(),
      billingHouseNumber: z.string().optional(),
      billingPostBox: z.string().optional(),
      billingPostalCode: z.string().optional(),
      billingCity: z.string().optional(),
      billingCanton: z.string().optional(),
      billingLanguage: z.string().optional(),

      // Email addresses (always required)
      emailInvoicesReminders: z
        .string()
        .min(
          1,
          tForms('billingAddress.validation.emailInvoicesRemindersRequired')
        )
        .email(
          tForms('billingAddress.validation.emailInvoicesRemindersInvalid')
        ),
      emailCoverageMoneyOrders: z
        .string()
        .min(
          1,
          tForms('billingAddress.validation.emailCoverageMoneyOrdersRequired')
        )
        .email(
          tForms('billingAddress.validation.emailCoverageMoneyOrdersInvalid')
        ),
      emailReporting: z
        .string()
        .min(1, tForms('billingAddress.validation.emailReportingRequired'))
        .email(tForms('billingAddress.validation.emailReportingInvalid')),
    })
    .superRefine((data, ctx) => {
      // If different address is enabled, make address fields required
      console.log(
        '🚀 ~ createBillingAddressSchema ~ data.isDifferentFromAccount:',
        data.isDifferentFromAccount
      );
      if (data.isDifferentFromAccount) {
        if (!data.billingName || data.billingName.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.nameRequired'),
            path: ['billingName'],
          });
        }
        if (!data.billingCountry || data.billingCountry.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.countryRequired'),
            path: ['billingCountry'],
          });
        }
        if (!data.billingStreet || data.billingStreet.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.streetRequired'),
            path: ['billingStreet'],
          });
        }
        if (!data.billingHouseNumber || data.billingHouseNumber.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.houseNumberRequired'),
            path: ['billingHouseNumber'],
          });
        }
        if (!data.billingPostalCode || data.billingPostalCode.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.postalCodeRequired'),
            path: ['billingPostalCode'],
          });
        }
        if (!data.billingCity || data.billingCity.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.cityRequired'),
            path: ['billingCity'],
          });
        }
        if (!data.billingLanguage || data.billingLanguage.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: tForms('billingAddress.validation.languageRequired'),
            path: ['billingLanguage'],
          });
        }

        // If country is Switzerland, canton is required
        if (
          data.billingCountry === 'CH' &&
          (!data.billingCanton || data.billingCanton.length === 0)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: tForms(
              'billingAddress.validation.cantonRequiredForSwitzerland'
            ),
            path: ['billingCanton'],
          });
        }
      }
    });

export type BillingAddressFormData = z.infer<
  ReturnType<typeof createBillingAddressSchema>
>;

interface UseBillingAddressFormProps {
  defaultValues?: Partial<BillingAddressFormData>;
  onSubmit?: (data: BillingAddressFormData) => void;
  onError?: (error: Error) => void;
  onChange?: (data: Partial<BillingAddressFormData>) => void;
}

export function useBillingAddressForm({
  defaultValues,
  onSubmit,
  onError,
  onChange,
}: UseBillingAddressFormProps = {}) {
  const { t: tForms } = useFormsTranslation();

  // Create schema with translations
  const billingAddressSchema = createBillingAddressSchema(tForms);

  const form = useForm<BillingAddressFormData>({
    resolver: zodResolver(billingAddressSchema),
    defaultValues: {
      isDifferentFromAccount: false,
      billingName: '',
      billingCountry: '',
      billingStreet: '',
      billingHouseNumber: '',
      billingPostBox: '',
      billingPostalCode: '',
      billingCity: '',
      billingLanguage: '',
      emailInvoicesReminders: '',
      emailCoverageMoneyOrders: '',
      emailReporting: '',
      ...defaultValues,
    },
    mode: 'onChange',
  });

  // Watch the switch to determine conditional field visibility
  const isDifferentFromAccount = useWatch({
    control: form.control,
    name: 'isDifferentFromAccount',
  });

  // Clear billing address fields when switch is turned off
  useEffect(() => {
    if (!isDifferentFromAccount) {
      form.setValue('billingName', '');
      form.setValue('billingCountry', '');
      form.setValue('billingStreet', '');
      form.setValue('billingHouseNumber', '');
      form.setValue('billingPostBox', '');
      form.setValue('billingPostalCode', '');
      form.setValue('billingCity', '');
      form.setValue('billingCanton', '');
      form.setValue('billingLanguage', '');
    }
  }, [isDifferentFromAccount, form]);

  // Clear canton field when country changes away from Switzerland
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'billingCountry' && value.billingCountry !== 'CH') {
        if (form.getValues('billingCanton')) {
          form.setValue('billingCanton', '');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (data: BillingAddressFormData) => {
      try {
        if (onSubmit) {
          await onSubmit(data);
        }
      } catch (error) {
        if (onError) {
          onError(
            error instanceof Error ? error : new Error('Submission failed')
          );
        }
      }
    },
    [onSubmit, onError]
  );

  // Handle form changes
  useEffect(() => {
    if (onChange) {
      const subscription = form.watch(data => {
        onChange(data as Partial<BillingAddressFormData>);
      });
      return () => subscription.unsubscribe();
    }
  }, [form, onChange]);

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isDifferentFromAccount,
    errors: form.formState.errors,
    isValid: form.formState.isValid,
    isSubmitting: form.formState.isSubmitting,
  };
}
